<template lang="pug">
.activity-table(v-if="!loading", @scroll="disablePlaceholder()")
  .activity-table__title {{ tableTitle }}
  .activity-table-wrapper
    .activity-table__row(v-for="employee in tableData", :key="employee.id")
      .activity-table__row-employee {{ employee.full_name }}
      .activity-table__row-works
        template(v-if="itsOneDay")
          .activity-table__row-works-item(
            v-for="work in employee.works",
            :key="`${employee.id}_${work.started_at}_${work.finished_at}`",
            :style="getWorkStyle(work)",
            :class="getClassByManual(work?.manual)",
            @mouseover="enablePlaceholder()",
            @mouseleave="disablePlaceholder()"
          )
            .activity-table__row-works-item-placeholder(v-if="canShowPlaceholder", :style="placeholderStyle")
              .activity-table__row-works-item-placeholder-time
                .activity-table__row-works-item-placeholder-time--circle(:style="getPlaceholderCircleStyle(work)")
                .activity-table__row-works-item-placeholder-time--total {{ work.worked_time }}
        template(v-else)
          .activity-table__row-works-item.days(
            v-for="(works, key) in employee.worksByDays",
            :key="`${employee.id}_${key}`",
            :style="getWorksStyle(key)",
            :class="getClassByManual(works?.manual)",
            @mouseover="enablePlaceholder()",
            @mouseleave="disablePlaceholder()"
          )
            .activity-table__row-works-item-placeholder(v-if="canShowPlaceholder", :style="placeholderStyle")
              .activity-table__row-works-item-placeholder-time
                .activity-table__row-works-item-placeholder-time--circle(:style="getPlaceholderCircleStyle(works)")
                .activity-table__row-works-item-placeholder-time--total {{ getWorksTime(works) }}
              .activity-table__row-works-item-placeholder-total {{ `${cleaningDashboardLocales.checkins}: ${works?.worksCount}` }}
        .activity-table__row-works-lines
          .activity-table__row-works-lines-item(
            v-for="(line, index) in xAxisMarks",
            :key="`${employee.id}_${index}_${line}`"
          )
    .activity-table__x-axis
      .activity-table__x-axis-item(
        v-for="(time, index) in xAxisMarks",
        :key="`${companyId}_${time}_${index}`",
        :style="getXaxisItemStyle(index)"
      ) {{ time }}
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { unitsLocales } from "@/services/useLocales";
import { cleaningDashboardLocales } from "@/services/useLocales";
import { parse } from "date-fns";

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: -1 },
  /* id текущей организации для фильтров. Пример: 1 */
  companyId: { type: Number, default: -1 },
  /** Фильтр даты от. Пример: "2025-10-28T21:00:00.000Z" */
  dateFrom: { type: String, default: "" },
  /** Фильтр даты до. Пример: "2025-10-29T21:00:00.000Z" */
  dateTo: { type: String, default: "" },
  /** Тип дат за которые запрашиваются данные. Пример: "today" */
  dateType: { type: String, default: "today" },
  /** Фильтр даты от при произвольном промежутке. Пример: "01.01.1970" */
  customDateFrom: { type: String, default: () => "" },
  /** Фильтр даты до при произвольном промежутке. Пример: "01.01.1970" */
  customDateTo: { type: String, default: () => "" },
});

const path = "/api/v3/cleaning/dashboard/activity";
const perPage = 20;

const dateType = computed(() => props.dateType);
const companyId = computed(() => props.companyId);
const customDateFrom = computed(() => props.customDateFrom);
const customDateTo = computed(() => props.customDateTo);
const dateFrom = computed(() => (props.dateType === "custom" ? customDateFrom.value : props.dateFrom));
const dateTo = computed(() => (props.dateType === "custom" ? customDateTo.value : props.dateTo));
const itsOneDay = computed(() => {
  const startedAt = new Date(dateFrom.value);
  const finishedAt = new Date(dateTo.value);
  startedAt.setHours(0, 0, 0, 0);
  finishedAt.setDate(finishedAt.getDate() - 1);
  return (
    (dateType.value === "custom" && Number(startedAt) === Number(finishedAt)) ||
    dateType.value === "today" ||
    dateType.value === "yesterday"
  );
});
const daysBetweenDates = computed(() => {
  const startedAtDate = new Date(dateFrom.value);
  const finishedAtDate = new Date(dateTo.value);
  const datesArray = [];
  while (startedAtDate < finishedAtDate) {
    datesArray.push(new Date(startedAtDate));
    startedAtDate.setDate(startedAtDate.getDate() + 1);
  }
  return datesArray;
});
const xAxisMarks = computed(() => {
  if (dateType.value === "today" || dateType.value === "yesterday" || dateType.value === "custom") {
    const startedAtDate = new Date(dateFrom.value);
    const finishedAtDate = new Date(dateTo.value);
    finishedAtDate.setHours(0, 0, 0, 0);
    startedAtDate.setHours(0, 0, 0, 0);
    if (itsOneDay.value) {
      let timesArray = Array.from({ length: 25 }, (_, i) => i);
      timesArray = timesArray
        .filter(item => item % 2 === 0)
        .map(item => {
          if (item < 10) {
            item = `0${item}`;
          }

          if (item === 24) {
            item = "00";
          }

          return `${item}:00`;
        });
      return timesArray;
    } else {
      return daysBetweenDates.value.map(date => date.getDate());
    }
  } else {
    const date = new Date(dateTo.value);
    // Нужен для исключениев случаев когда меняя месяц не следующий и имея дату 31, а в месяце 30, он перескочит сразу на 2 месяца
    date.setDate(1);
    // Устанавливаем следующий месяц
    date.setMonth(date.getMonth() + 1);
    // Устанавливаем день на 0, чтобы получить последний день предыдущего месяца
    date.setDate(0);
    const daysInMonth = date.getDate();
    return Array.from({ length: daysInMonth + 1 }, (_, i) => i);
  }
});

const loading = ref(false);
const tableTitle = ref("");
const tableData = ref([]);
const page = ref(1);
const hasNextPage = ref(false);
const canShowPlaceholder = ref(true);
const placeholderStyle = ref({});

const disablePlaceholder = () => {
  canShowPlaceholder.value = false;
  placeholderStyle.value = {};
};

const enablePlaceholder = () => {
  canShowPlaceholder.value = true;
  const target = event.target;
  let tabCeilElRect;
  if (target.classList.contains("activity-table__row-works-item")) {
    tabCeilElRect = target.getBoundingClientRect();
  } else {
    tabCeilElRect = target.closest(".activity-table__row-works-item").getBoundingClientRect();
  }
  placeholderStyle.value = {
    left: `${tabCeilElRect.left + tabCeilElRect.width / 2}px`,
    top: `${tabCeilElRect.top}px`,
  };
};

const getPlaceholderCircleStyle = work => {
  if (work?.manual === false) {
    return {
      backgroundColor: "#5b7aff",
    };
  } else {
    return {
      backgroundColor: "#14c783",
    };
  }
};

const getClassByManual = manual => {
  if (!manual) {
    return "dark-blue";
  }
  return "";
};

const getContentAsPercent = (item, width) => {
  return (item / width) * 100;
};

const getWorkStyle = work => {
  // Получаем ширину всей таблице в виде милисекунд за весь таймлайн
  const dateFromDate = new Date(dateFrom.value);
  const endOfDate = new Date(dateTo.value);

  if (dateType.value === "today") {
    endOfDate.setHours(24, 0, 0, 0);
  }

  const fullTableWidth = endOfDate - dateFromDate;
  // Получаем ширину ячейки таблицы в виде миллисекунд за ее время существования на этом таймлайне
  const startedAtDate = new Date(work.started_at);
  const finishedAtDate = new Date(work.finished_at);
  const workCeilWidth = finishedAtDate - startedAtDate;
  // Получаем процентное соотношение чтобы получить процентный размер ячейки
  const workCeilWidthAsPercent = getContentAsPercent(workCeilWidth, fullTableWidth);
  const marginLeftCeil = startedAtDate - dateFromDate;
  const marginLeftCeilAsPercent = getContentAsPercent(marginLeftCeil, fullTableWidth);
  return {
    width: `${workCeilWidthAsPercent}%`,
    marginLeft: `${marginLeftCeilAsPercent}%`,
  };
};

const getWorksStyle = date => {
  if (dateType.value === "custom") {
    const dateFromDate = new Date(dateFrom.value);
    const endOfDate = new Date(dateTo.value);
    endOfDate.setHours(0, 0, 0, 0);
    const fullTableWidth = endOfDate - dateFromDate;
    const startedAtDate = new Date(date);
    startedAtDate.setHours(0, 0, 0, 0);
    const marginLeftCeil = startedAtDate - dateFromDate;
    const marginLeftCeilAsPercent = getContentAsPercent(marginLeftCeil, fullTableWidth);

    return {
      marginLeft: `calc(${marginLeftCeilAsPercent}% - 12px)`,
    };
  } else {
    const jsDate = new Date(date);
    const dayOfDate = jsDate.getDate();
    // Нужен для исключениев случаев когда меняя месяц не следующий и имея дату 31, а в месяце 30, он перескочит сразу на 2 месяца
    jsDate.setDate(1);
    // Устанавливаем следующий месяц
    jsDate.setMonth(jsDate.getMonth() + 1);
    // Устанавливаем день на 0, чтобы получить последний день предыдущего месяца
    jsDate.setDate(0);
    const daysInTable = jsDate.getDate();

    return {
      marginLeft: `calc(${(100 / daysInTable) * dayOfDate}% - 12px)`,
    };
  }
};

const getWorksTime = works => {
  let worksTimeAsSeconds = works.workedTime / 1000;
  let hours = 0;
  let minutes = 0;

  if (worksTimeAsSeconds >= 3600) {
    hours = Math.floor(worksTimeAsSeconds / 3600);
    worksTimeAsSeconds = worksTimeAsSeconds % 3600;
  }

  if (worksTimeAsSeconds >= 60) {
    minutes = Math.floor(worksTimeAsSeconds / 60);
  }
  return `${hours} ${unitsLocales.value.hour}, ${minutes} ${unitsLocales.value.short_minutes}.`;
};

const getXaxisItemStyle = index => {
  const marginPerIndex = 100 / (xAxisMarks.value.length - 1);
  return {
    left: `${marginPerIndex * index}%`,
  };
};

const getFilterWorksByDays = () => {
  tableData.value = tableData.value.map(employee => {
    employee["worksByDays"] = {};
    employee.works.map(work => {
      const workStarted = new Date(work.started_at);
      const workFinished = new Date(work.finished_at);
      const date = `${workStarted.getFullYear()}-${workStarted.getMonth() + 1}-${workStarted.getDate()}`;

      if (employee["worksByDays"][date]) {
        employee["worksByDays"][date] = {
          workedTime: employee["worksByDays"][date].workedTime + (workFinished - workStarted),
          worksCount: employee["worksByDays"][date].worksCount + 1,
        };
      } else {
        employee["worksByDays"][date] = {
          workedTime: workFinished - workStarted,
          worksCount: 1,
        };
      }

      if (work.manual && employee["worksByDays"][date]["manual"] === undefined) {
        employee["worksByDays"][date]["manual"] = true;
      } else {
        employee["worksByDays"][date]["manual"] = false;
      }
    });
    return employee;
  });
};

const getData = async () => {
  if (!loading.value) {
    try {
      loading.value = true;

      const params = {
        company_ids: [companyId.value],
        facility_id: props.facilityId,
        page: page.value,
        per_page: perPage,
        date_from: dateFrom.value,
        date_to: dateTo.value,
      };

      const { data } = await backend.index(path, { params }, { encodeNestedData: true });

      tableData.value = data.data;
      tableTitle.value = data?.labels?.title ?? "";

      if (dateType !== "today" || dateType !== "yesterday") {
        getFilterWorksByDays();
      }

      const pagination = data.pagination;

      page.value++;
      hasNextPage.value = pagination.total - perPage * pagination.page > 0;
    } catch (err) {
      await handleError(err, { notify: false });
    } finally {
      nextTick(() => {
        loading.value = false;
      });
    }
  }
};

const reachedEndOfList = reached => {
  if (reached) getData();
};

onMounted(() => {
  getData();
});
</script>

<style lang="scss">
/* Глобальные стили для q-input, т.к. квазар не видит стили из scoped */
</style>

<style lang="scss" scoped>
.activity-table {
  $employeWidth: 262px;
  $rowGap: 55px;

  padding: 24px 24px 0 24px;
  margin-top: 8px;
  border-radius: 20px;
  border: 1px solid #aeb5c4;
  max-height: 600px;
  overflow-y: scroll;

  &::-webkit-scrollbar-track {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  &__title {
    font-size: 20px;
    margin-bottom: 22px;
    color: var(--cleaning-dashboard-text-color);
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: $rowGap;
    height: 24px;
    margin-bottom: 8px;

    &:hover {
      background-color: var(--cleaning-dashboard-table-title-background);
    }

    &-employee {
      min-width: $employeWidth;
      height: 24px;
      border-radius: 8px;
      padding: 4px 8px;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: end;
      background-color: #dee4ff;
    }

    &-works {
      height: 24px;
      width: 100%;
      display: flex;
      align-items: center;
      position: relative;

      &-item {
        height: 16px;
        border-radius: 4px;
        background-color: rgba(20, 199, 131, 0.5);
        z-index: 2;
        position: absolute;

        &.dark-blue {
          background-color: rgba(91, 122, 255, 0.5);
        }

        &.days {
          width: 24px;
        }

        &:hover {
          .activity-table__row-works-item-placeholder {
            display: block;
          }
        }

        &-placeholder {
          position: fixed;
          padding: 20px;
          background-color: var(--dashboard-card-background);
          border-radius: 12px;
          box-shadow: 0px 0px 24px 0px #2c3f581f;
          transform: translate(-50%, calc(-100% - 15px));
          z-index: 10;
          display: none;
          min-width: fit-content;
          color: var(--cleaning-dashboard-text-color);

          &::after {
            content: "";
            width: 20px;
            height: 20px;
            display: block;
            z-index: 1;
            position: absolute;
            border: 11px solid transparent;
            border-top: 11px solid var(--dashboard-card-background);
            bottom: -22px;
            left: 50%;
            transform: translateX(-50%);
          }

          &-time {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 6px;

            &--circle {
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background-color: #14c783;
              min-width: 10px;
            }

            &--total {
              font-size: 15px;
              width: fit-content;
              white-space: nowrap;
            }
          }
        }
      }

      &-lines {
        position: absolute;
        left: 0;
        width: 100%;
        display: flex;
        justify-content: space-between;
        height: 100%;

        &-item {
          width: 1px;
          height: calc(100% + 8px);
          background-color: #d7dae1;
        }
      }
    }
  }

  &__x-axis {
    margin-left: calc($employeWidth + $rowGap);
    font-size: 12px;
    height: 45px;
    position: sticky;
    background-color: var(--cleaning-dashboard-block-background);
    width: calc(100% - $employeWidth - $rowGap);
    z-index: 3;
    color: #78839c;
    bottom: 0;
    padding-bottom: 24px;

    &::before {
      content: "";
      position: absolute;
      left: -13px;
      width: calc(100% + 26px);
      height: 45px;
      z-index: 2;
      background-color: var(--cleaning-dashboard-block-background);
    }

    &-item {
      position: absolute;
      transform: translateX(-50%);
      z-index: 3;
    }
  }
}
</style>
